import { IPartnerFinSobRow } from '../../model/types'
import { OVERALL_CATEGORY } from '../revenueAndSob/constants'

import { TRevenueAndSobProductLevel } from './types'

/** The product hierarchy's levels, shallow to deep. Every level past `offering` is optional on
 *  `IPartnerFinSobRow` and reads as the `OVERALL_CATEGORY` sentinel when the row doesn't carry
 *  it. */
export const PRODUCT_LEVELS: TRevenueAndSobProductLevel[] = [
  'offering',
  'category',
  'subCategory',
  'l3Category',
]

/** True for whatever marks "no breakdown at this level" — the explicit `OVERALL_CATEGORY` sentinel
 *  a row can carry, or the field simply being absent, which means the same thing. */
export const isOverallLevelValue = (value?: string | null): boolean =>
  !value || value === OVERALL_CATEGORY

/** The product-hierarchy fields below `offering` — the ones that are optional on the row and
 *  default to `OVERALL_CATEGORY`. Scoped to this file only to give `getLevelValue` a type-safe way
 *  to index `row` by one of these three keys. */
type TDeeperProductLevel = Exclude<TRevenueAndSobProductLevel, 'offering'>

/** The row's value at one product level. `offering` is a required field with no "overall" state of
 *  its own, so it's always read directly; every deeper level falls back to `OVERALL_CATEGORY` when
 *  the row doesn't carry it. */
export const getLevelValue = (
  row: IPartnerFinSobRow,
  field: TRevenueAndSobProductLevel
): string => {
  if (field === 'offering') {
    return row.offering
  }

  return row[field as TDeeperProductLevel] ?? OVERALL_CATEGORY
}

/** The row's value at every level from `offering` down to `levelIndex`, e.g. `['PC',
 *  'jbp-pc-commercial']` for `levelIndex === 1`. */
export const getProductPath = (row: IPartnerFinSobRow, levelIndex: number): string[] =>
  PRODUCT_LEVELS.slice(0, levelIndex + 1).map((level) => getLevelValue(row, level))

/** True when `row` is the backend's own pre-aggregated total for a node at `levelIndex` — its own
 *  level is concrete (always true for `offering`, which has no overall state) and the next level
 *  down is the `OVERALL_CATEGORY` sentinel. `l3Category`, the deepest level, has no level below it
 *  to check, so that half of the condition is vacuously true there. */
export const isNodeDefiningRow = (row: IPartnerFinSobRow, levelIndex: number): boolean => {
  const ownField = PRODUCT_LEVELS[levelIndex]
  const ownFieldIsConcrete = levelIndex === 0 || !isOverallLevelValue(getLevelValue(row, ownField))

  const isDeepestLevel = levelIndex === PRODUCT_LEVELS.length - 1
  const childFieldIsOverall =
    isDeepestLevel || isOverallLevelValue(getLevelValue(row, PRODUCT_LEVELS[levelIndex + 1]))

  return ownFieldIsConcrete && childFieldIsOverall
}
